import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EditProposalDto, FetchError, IssueDto } from "../../../types";
import { queryKeys, useAccessToken } from "../../..";
import { configuration } from "../../../../configuration";
import { useSignalR } from "../../../../hooks";
import { optimisticallyUpdateQueryData } from "../../utilities";

type Variables = {
  proposalGuid: string;
  issueGuid: string;
  description: string;
};

const useEditProposalRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editProposalRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditProposalDto = {
      description: variables.description,
    };

    const response = await fetch(`${configuration.apiRootUrl}/proposals/${variables.proposalGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editProposalRequest };
};

const useEditProposal = () => {
  const { editProposalRequest } = useEditProposalRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editProposalRequest,
    onMutate: async (variables) => {
      let cachedIssueData: IssueDto | undefined;

      cachedIssueData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          proposals: cachedData.proposals.map((proposal) => {
            if (proposal.guid === variables.proposalGuid) {
              return {
                ...proposal,
                description: variables.description,
              };
            }

            return proposal;
          }),
        })
      );

      return { cachedIssueData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchComments(variables.issueGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueData);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));
    },
  });
};

export { useEditProposal };
